import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type AuditEvent = components['schemas']['AuditEvent']

function auditSubject(e: AuditEvent): string {
  const parts = [e.subject, e.old_value && e.new_value ? `${e.old_value} → ${e.new_value}` : undefined, e.note].filter(Boolean)
  return parts.length > 0 ? parts.join(' — ') : '—'
}

function fromParam(date: string): string | undefined {
  return date ? `${date}T00:00:00.000Z` : undefined
}

function toParam(date: string): string | undefined {
  if (!date) return undefined
  const d = new Date(`${date}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString()
}

function AuditTable({
  title,
  dataAct,
  rows,
  pager,
  hasNext,
  loading,
  onNext,
  onRefresh,
  onExport,
  exportPending,
  from,
  to,
  onFromChange,
  onToChange,
  onClearFilters,
}: {
  title: string
  dataAct: ReturnType<typeof actionId>
  rows: readonly AuditEvent[]
  pager: ReturnType<typeof useCursorPager>
  hasNext: boolean
  loading: boolean
  onNext: () => void
  onRefresh: () => void
  onExport: () => void
  exportPending: boolean
  from: string
  to: string
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
  onClearFilters: () => void
}) {
  return (
    <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">{title}</h3>
        <div data-cmp="grid-tools">
          <Button data-act={undefined} ghost disabled={!from && !to} onClick={onClearFilters}>
            Reset filters
          </Button>
          <IconButton data-act={undefined} icon="i-filter-off" label="Clear filters" disabled={!from && !to} onClick={onClearFilters} />
          <IconButton data-act={undefined} icon="i-refresh" label="Refresh" disabled={loading} onClick={onRefresh} />
          <Button data-act={dataAct} ghost disabled={exportPending} onClick={onExport}>
            Export CSV
          </Button>
        </div>
      </div>
      <div data-cmp="grid-filter-row" className="flex items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-small font-semibold text-tx2">
          From
          <input type="date" value={from} onChange={(e) => onFromChange(e.target.value)} className="border border-bd2 rounded-1 py-2 px-3 text-small" />
        </label>
        <label className="flex items-center gap-2 text-small font-semibold text-tx2">
          To
          <input type="date" value={to} onChange={(e) => onToChange(e.target.value)} className="border border-bd2 rounded-1 py-2 px-3 text-small" />
        </label>
      </div>
      <GridTable
        columns={[
          {
            key: 'event',
            header: 'Event',
            render: (e) => (
              <span className="font-mono" title={`${e.audit_event_id} · ${formatJobTime(e.occurred_at)}`}>
                {e.action} <span className="text-muted">({e.handle})</span>
              </span>
            ),
          },
          { key: 'details', header: 'Event details', render: (e) => auditSubject(e) },
          { key: 'timestamp', header: 'Timestamp (UTC)', render: (e) => formatJobTime(e.occurred_at) },
          {
            key: 'name',
            header: 'Name',
            render: (e) =>
              e.actor_type === 'SYSTEM' ? (
                'System'
              ) : e.actor_type === 'INTERNAL_OPS' ? (
                'Internal ops'
              ) : e.actor && (e.actor.display_name || e.actor.email) ? (
                <>
                  <Avatar name={e.actor.display_name ?? e.actor.email!} />
                  {e.actor.display_name ?? e.actor.email}
                </>
              ) : (
                'System'
              ),
          },
        ]}
        rows={rows}
        getRowKey={(e) => e.audit_event_id}
        emptyMessage={loading ? '' : 'No events match these filters.'}
      />
      <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between mt-5">
        <Note>Every operation is recorded with its initiator, parameters and result. Secret values are never recorded, and the log cannot be edited or deleted here.</Note>
        <Pager hasPrev={pager.hasPrev} hasNext={hasNext} loading={loading} onPrev={pager.goPrev} onNext={onNext} />
      </div>
    </div>
  )
}

/**
 * `v-auditprod` — process/09-SCREEN-SPECS/v-auditprod.md. **Both trails, one screen** —
 * `AuditEvent.scope` is what tells an organisation row from a workspace row apart, and there are
 * exactly two audit tables, never a third. The identifier column shows only the 12-character
 * `handle`, never `audit_event_id`, and carries no sort affordance (rule 1) — rows order by
 * `occurred_at`, newest first, always. A system actor renders as **System**, never a blank person
 * (rule 8). Both exports are audited egress, the same `parseAs: 'blob'` pattern already built four
 * times this build (`v-backupjob`/`v-restorejob`/`v-drift`/reports).
 *
 * **A real "Refresh" `icon-button` per trail added W157** — the spec's own text names it
 * explicitly: *"Every other control on this screen is a read and carries none — the two filters,
 * the cursor, the refresh, the column chooser and Reset filters."*
 *
 * **`avatar` added W161** — the spec's own words: "the actor initials beside a person's name."
 * Shown only for a real human actor, never for `SYSTEM`/`INTERNAL_OPS` (rule 8 above still holds).
 *
 * **`grid-filter-row` added, one real half of two (W170).** The spec's own
 * Components table calls this row "event-type and time-range filters." Reading
 * `organisationListAuditEvents`/`workspaceListAuditEvents` directly: both carry real `from`/`to`
 * date-time parameters (per trail, independent), but neither carries an event-type/`action` filter
 * anywhere in its contract — so only the time-range half is built, wired to a real query parameter
 * per §24.2 rule 25 ("changing any filter invalidates the cursor," hence `pager.reset()` on every
 * change), and the event-type half is left unbuilt rather than invented. A real "Clear filters"
 * `icon-button` was added alongside it, now that there is a real filter to clear — the same fix
 * `v-jobsbackup` got at W157, no `data-act` since the spec's own Actions table names only the two
 * exports. The column chooser remains not built: no column-visibility state exists anywhere to back
 * one.
 */
export function VAuditprod() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const orgPager = useCursorPager()
  const wsPager = useCursorPager()
  const [orgFrom, setOrgFrom] = useState('')
  const [orgTo, setOrgTo] = useState('')
  const [wsFrom, setWsFrom] = useState('')
  const [wsTo, setWsTo] = useState('')

  const orgEvents = useQuery({
    queryKey: ['org-audit', orgId, orgPager.cursor, orgFrom, orgTo],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/audit', {
        params: { path: { org_id: orgId }, query: { cursor: orgPager.cursor, from: fromParam(orgFrom), to: toParam(orgTo) } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const wsEvents = useQuery({
    queryKey: ['workspace-audit', orgId, workspaceId, wsPager.cursor, wsFrom, wsTo],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/audit', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: wsPager.cursor, from: fromParam(wsFrom), to: toParam(wsTo) } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  function download(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportOrg = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/audit/export', { params: { path: { org_id: orgId } }, parseAs: 'blob' })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      download(response.data as unknown as Blob, 'organisation-audit.csv')
    },
  })

  const exportWs = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/audit/export', { params: { path: { org_id: orgId, workspace_id: workspaceId } }, parseAs: 'blob' })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      download(response.data as unknown as Blob, 'workspace-audit.csv')
    },
  })

  const orgProblem = orgEvents.error instanceof ApiProblemError ? orgEvents.error.problem : undefined
  const wsProblem = wsEvents.error instanceof ApiProblemError ? wsEvents.error.problem : undefined

  return (
    <div data-scr={unitId('v-auditprod')}>
      <h1 className="m-0 mb-8 text-section font-semibold">Audit log: this application</h1>

      {orgProblem && (isUnexpectedRefusal(orgProblem.refusal_kind) ? <UnexpectedRefusal problem={orgProblem} /> : <Note>{orgProblem.title}</Note>)}
      <AuditTable
        title="Organisation trail"
        dataAct={actionId('organisationAudit.export')}
        rows={(orgEvents.data?.items ?? []) as unknown as readonly AuditEvent[]}
        pager={orgPager}
        hasNext={Boolean(orgEvents.data?.next_cursor)}
        loading={orgEvents.isFetching}
        onNext={() => {
          if (orgEvents.data?.next_cursor) orgPager.goNext(orgEvents.data.next_cursor)
        }}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ['org-audit', orgId] })}
        onExport={() => exportOrg.mutate()}
        exportPending={exportOrg.isPending}
        from={orgFrom}
        to={orgTo}
        onFromChange={(v) => {
          orgPager.reset()
          setOrgFrom(v)
        }}
        onToChange={(v) => {
          orgPager.reset()
          setOrgTo(v)
        }}
        onClearFilters={() => {
          orgPager.reset()
          setOrgFrom('')
          setOrgTo('')
        }}
      />

      {wsProblem && (isUnexpectedRefusal(wsProblem.refusal_kind) ? <UnexpectedRefusal problem={wsProblem} /> : <Note>{wsProblem.title}</Note>)}
      <AuditTable
        title="Workspace trail"
        dataAct={actionId('workspaceAudit.export')}
        rows={(wsEvents.data?.items ?? []) as unknown as readonly AuditEvent[]}
        pager={wsPager}
        hasNext={Boolean(wsEvents.data?.next_cursor)}
        loading={wsEvents.isFetching}
        onNext={() => {
          if (wsEvents.data?.next_cursor) wsPager.goNext(wsEvents.data.next_cursor)
        }}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ['workspace-audit', orgId, workspaceId] })}
        onExport={() => exportWs.mutate()}
        exportPending={exportWs.isPending}
        from={wsFrom}
        to={wsTo}
        onFromChange={(v) => {
          wsPager.reset()
          setWsFrom(v)
        }}
        onToChange={(v) => {
          wsPager.reset()
          setWsTo(v)
        }}
        onClearFilters={() => {
          wsPager.reset()
          setWsFrom('')
          setWsTo('')
        }}
      />
    </div>
  )
}
